import React, { lazy, Suspense, useState, useRef, useEffect, useCallback } from 'react';
import { Confirmation, TwIconButton } from 'components/common';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import {
  FaArrowLeft, FaArrowUp, FaList, FaPrint, FaClone, FaEdit,
  FaEllipsisV, FaShareSquare, FaClosedCaptioning, FaTrashAlt,
} from 'react-icons/fa';
import {
  MdCancel, MdNotificationsActive, MdOutlineLockReset,
  MdOutlineSettingsBackupRestore,
} from 'react-icons/md';
import { RiGitRepositoryCommitsLine } from 'react-icons/ri';
import { cancelWaiver, deleteWaiver, remindToSalesWaiver, resetWaiver, rollbackWaiver, sendToApproveWaiver } from 'redux/crm/project/waiverSlice';
import PropTypes from 'prop-types';

const LazyWaiverClearWaiverForm = lazy(() => import("./WaiverClearWaiverForm"));
const LazyWaiverReSubmitToCSDForm = lazy(() => import("./WaiverReSubmitToCSDForm"));
const LazyCloneWaiverConfirm = lazy(() => import("./CloneWaiverConfirm"));
const LazyChangeCommitmentForm = lazy(() => import("./ChangeCommitmentForm"));

// ── Dropdown item / group ─────────────────────────────────────────────────────
const DropdownItem = ({ icon: Icon, label, onClick, danger = false }) => (
  <button
    type="button"
    onClick={onClick}
    className={`tw-flex tw-items-center tw-gap-2.5 tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-left tw-bg-transparent tw-border-0 tw-cursor-pointer tw-transition-colors tw-rounded tw-my-0.5 ${
      danger ? 'tw-text-red-600 hover:tw-bg-red-50' : 'tw-text-slate-700 hover:tw-bg-slate-50'
    }`}
    style={{ width: 'calc(100% - 8px)', marginLeft: 4 }}
  >
    <span className={`tw-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-rounded-md ${danger ? 'tw-bg-red-50' : 'tw-bg-slate-100'}`}>
      <Icon size={12} className={danger ? 'tw-text-red-500' : 'tw-text-slate-500'} />
    </span>
    {label}
  </button>
);
DropdownItem.propTypes = {
  icon:    PropTypes.elementType.isRequired,
  label:   PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  danger:  PropTypes.bool,
};

const DropdownGroup = ({ items, isLast }) => (
  <div className="tw-px-1">
    {items.map((item, i) => (
      <DropdownItem key={i} {...item} />
    ))}
    {!isLast && <div className="tw-border-t tw-border-slate-100 tw-my-1" />}
  </div>
);
DropdownGroup.propTypes = {
  items:  PropTypes.array.isRequired,
  isLast: PropTypes.bool,
};

const WaiverDetailHeader = ({waiver}) => {
  const [showConfirmationDelete, setShowConfirmationDelete] = useState(false);
  const [showResubmitToCSDForm, setShowResubmitToCSDForm] = useState(false);
  const [messageConfirm, setMessageConfirm] = useState(null);
  const [showCommitmentForm, setShowCommitmentForm] = useState(false);
  const [showClearWaiverForm, setShowClearWaiverForm] = useState(false);
  const [showCloneForm, setShowCloneForm] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const dropdownRef = useRef(null);

  const project = waiver?.project;
  const permissions = waiver?.permissions;

  const {
    allow_edit,
    allow_delete,
    allow_send_to_approve,
    allow_resubmit_approval_to_chief_rep,
    allow_resubmit_to_csd,
    allow_clear,
    allow_send_reminder_to_sales,
    allow_rollback,
    allow_reset,
    allow_cancel,
    allow_print,
    allow_update_commitment
  } = permissions || {};

  const projectUrl = "/crm/projects";
  const projectId = project?.id;
  const waiverId = waiver?.id;

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const closeDropdown = useCallback(() => setDropdownOpen(false), []);

  const handlePrintWaiverForm = () => {
    navigate(`${projectUrl}/${projectId}/waivers/${waiverId}/print`, {state: {waiver}})
  }

  const handleEditWaiver = () => {
    navigate(`${projectUrl}/${projectId}/waivers/${waiverId}/edit`, {state: {waiver}})
  }

  const handleProject = () => {
    navigate(`${projectUrl}/${projectId}`)
  }

  const confirmMsg = (message) => {
    setMessageConfirm(message)
    setShowConfirmationDelete(true)
  }

  const closeConfirmModal = () => {
    setShowConfirmationDelete(false)
  }

  const handleCloseConfirmation = () => {
    closeConfirmModal()
  }

  const handleActionConfirm = async () => {
    try {
      dispatch(deleteWaiver({projectId, waiverId}))

      if(location && location.state && location.state.source_waiver) {
        navigate(`${projectUrl}/waivers`)
      }
      else {
        navigate(`${projectUrl}/${projectId}`)
      }

      closeConfirmModal()
    }
    catch(error) {
      toast.error(error.message)
    }
  }

  const handleDeleteWaiver = () => {
    confirmMsg(
      <span>Are you sure to delete project ID#<strong className="tw-text-red-600">{projectId}</strong>?</span>
    );
  }

  const handleRequestApproval = () => {
    dispatch(sendToApproveWaiver({projectId, waiverId}))
  }

  const handleResubmitApprovalToChiefRep = () => {
    dispatch(sendToApproveWaiver({projectId, waiverId})) //need to check
  }

  const handleResubmitToCSD = () => {
    setShowResubmitToCSDForm(true)
  }

  const handleClearWaiver = () => {
    setShowClearWaiverForm(true)
  }

  const handleSendReminderToSales = () => {
    dispatch(remindToSalesWaiver({projectId, waiverId}))
  }

  const handleRollback = () => {
    dispatch(rollbackWaiver({projectId, waiverId}))
  }

  const handleResetWaiver = () => {
    dispatch(resetWaiver({projectId, waiverId}))
  }

  const handleCancelWaiver = () => {
    dispatch(cancelWaiver({projectId, waiverId}))
  }

  const handleWaiverList = () => {
    navigate(`${projectUrl}/waivers`)
  }

  const handleCloneWaiver = () => {
    setShowCloneForm(true)
  }

  const handleChangeCommitment = () => {
    setShowCommitmentForm(true)
  }

  const withClose = (fn) => () => { fn(); closeDropdown(); };

  // Build the action-menu groups (dividers between groups), filtering by permission.
  const menuGroups = [
    {
      items: [
        allow_send_to_approve && {
          icon: FaShareSquare, label: 'Request approval', onClick: withClose(handleRequestApproval),
        },
        allow_resubmit_approval_to_chief_rep && {
          icon: FaShareSquare, label: 'Resubmit approval to Chief Rep', onClick: withClose(handleResubmitApprovalToChiefRep),
        },
      ].filter(Boolean),
    },
    {
      items: [
        allow_resubmit_to_csd && {
          icon: FaShareSquare, label: 'Resubmit to CSD', onClick: withClose(handleResubmitToCSD),
        },
      ].filter(Boolean),
    },
    {
      items: [
        allow_clear && {
          icon: FaClosedCaptioning, label: 'Clear waiver', onClick: withClose(handleClearWaiver),
        },
        allow_send_reminder_to_sales && {
          icon: MdNotificationsActive, label: 'Send Reminder to Sales', onClick: withClose(handleSendReminderToSales),
        },
      ].filter(Boolean),
    },
    {
      items: [
        allow_update_commitment && {
          icon: RiGitRepositoryCommitsLine, label: 'Change Commitment', onClick: withClose(handleChangeCommitment),
        },
      ].filter(Boolean),
    },
    {
      items: [
        allow_rollback && {
          icon: MdOutlineSettingsBackupRestore, label: 'Rollback', onClick: withClose(handleRollback),
        },
        allow_reset && {
          icon: MdOutlineLockReset, label: 'Reset', onClick: withClose(handleResetWaiver),
        },
        allow_cancel && {
          icon: MdCancel, label: 'Cancel', onClick: withClose(handleCancelWaiver), danger: true,
        },
      ].filter(Boolean),
    },
    {
      items: [
        allow_edit && {
          icon: FaEdit, label: 'Edit', onClick: withClose(handleEditWaiver),
        },
      ].filter(Boolean),
    },
    {
      items: [
        allow_delete && {
          icon: FaTrashAlt, label: 'Delete Waiver', onClick: withClose(handleDeleteWaiver), danger: true,
        },
      ].filter(Boolean),
    },
  ].filter(g => g.items.length > 0);

  const statusName = waiver?.status?.name;
  const isRejected = statusName === "Rejected";

  return (
    <>
      {/* No overflow-hidden here: the ⋮ actions menu is absolutely positioned and
          drops below the toolbar into the card body — overflow-hidden would clip its
          last item (e.g. "Rollback"). Rounded corners are preserved by the border/bg. */}
      <div className="tw-mb-3 tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-shadow-sm">

        {/* ── Toolbar ── */}
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-3 tw-py-2 tw-border-b tw-border-slate-200">
          <TwIconButton icon={FaArrowLeft} iconSize={12} variant="secondary" size="lg" onClick={() => navigate(-1)} title="Back" />

          <div className="tw-flex tw-items-center tw-gap-1.5">
            {allow_print && (
              <TwIconButton icon={FaPrint} iconSize={12} variant="secondary" size="lg" onClick={handlePrintWaiverForm} title="Waiver Form" />
            )}

            <TwIconButton icon={FaClone} iconSize={12} variant="secondary" size="lg" onClick={handleCloneWaiver} title="Clone" />

            {allow_edit && (
              <TwIconButton icon={FaEdit} iconSize={12} variant="secondary" size="lg" onClick={handleEditWaiver} title="Edit" />
            )}

            <TwIconButton icon={FaArrowUp} iconSize={12} variant="secondary" size="lg" onClick={handleProject} title="Back to Project" />

            <TwIconButton icon={FaList} iconSize={12} variant="secondary" size="lg" onClick={handleWaiverList} title="Waiver List" />

            {menuGroups.length > 0 && (
              <div ref={dropdownRef} className="tw-relative">
                <TwIconButton icon={FaEllipsisV} iconSize={11} variant="secondary" size="lg" onClick={() => setDropdownOpen(o => !o)} title="More actions" />

                {dropdownOpen && (
                  <div className="tw-absolute tw-right-0 tw-top-full tw-mt-1.5 tw-w-60 tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-shadow-xl tw-z-50 tw-py-1">
                    {menuGroups.map((group, gi) => (
                      <DropdownGroup
                        key={gi}
                        items={group.items}
                        isLast={gi === menuGroups.length - 1}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── Title ── */}
        <div className="tw-px-3 tw-py-4 tw-text-center">
          <h5 className="tw-text-2xl tw-font-semibold tw-text-slate-800 tw-mb-1">WAIVER FORM</h5>
          <div className="tw-text-sm tw-text-slate-600">
            No.: <span className="tw-text-blue-600">{waiver?.waiver_number}</span>
          </div>
          <div className="tw-text-sm tw-text-slate-600 tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-mt-1">
            <span>Status.:</span>
            {statusName && (
              <span className={`tw-inline-flex tw-items-center tw-px-2.5 tw-py-0.5 tw-rounded-xl tw-text-xs tw-font-semibold ${
                isRejected ? 'tw-bg-red-50 tw-text-red-700' : 'tw-bg-blue-50 tw-text-blue-700'
              }`}>
                {statusName}
              </span>
            )}
          </div>
        </div>
      </div>

      <Confirmation
        show={showConfirmationDelete}
        message={messageConfirm}
        onConfirm={handleActionConfirm}
        onCancel={handleCloseConfirmation}
      />

      <Suspense fallback={<div>Loading...</div>}>
        {showClearWaiverForm && (
          <LazyWaiverClearWaiverForm
            showModal={showClearWaiverForm}
            setshowModal={setShowClearWaiverForm}
            waiver={waiver}
          />
        )}

        {showResubmitToCSDForm && (
          <LazyWaiverReSubmitToCSDForm
            showModal={showResubmitToCSDForm}
            setshowModal={setShowResubmitToCSDForm}
            waiver={waiver}
          />
        )}

        {showCloneForm && (
          <LazyCloneWaiverConfirm
            showModal={showCloneForm}
            setshowModal={setShowCloneForm}
            waiver={waiver}
          />
        )}

        {showCommitmentForm && (
          <LazyChangeCommitmentForm
            showModal={showCommitmentForm}
            setshowModal={setShowCommitmentForm}
            waiver={waiver}
          />
        )}
      </Suspense>
    </>
  );
};

WaiverDetailHeader.propTypes = {
  waiver: PropTypes.shape({
    id: PropTypes.string.isRequired,
    waiver_number: PropTypes.string.isRequired,
    project: PropTypes.shape({
      id: PropTypes.string
    }),
    status: PropTypes.shape({
      name: PropTypes.string
    }),
    permissions: PropTypes.shape({
      allow_edit: PropTypes.bool,
      allow_delete: PropTypes.bool,
      allow_send_to_approve: PropTypes.bool,
      allow_resubmit_approval_to_chief_rep: PropTypes.bool,
      allow_resubmit_to_csd: PropTypes.bool,
      allow_clear: PropTypes.bool,
      allow_send_reminder_to_sales: PropTypes.bool,
      allow_rollback: PropTypes.bool,
      allow_reset: PropTypes.bool,
      allow_cancel: PropTypes.bool,
      allow_print: PropTypes.bool
    })
  }).isRequired
};

export default WaiverDetailHeader;
